import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Images, 
  Trash2, 
  Edit, 
  Eye, 
  EyeOff, 
  Star,
  Upload,
  X,
  Loader2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const CATEGORIES = [
  { value: "wedding", label: "Wedding" },
  { value: "portrait", label: "Portrait" },
  { value: "event", label: "Event" },
  { value: "commercial", label: "Commercial" },
  { value: "nature", label: "Nature" },
  { value: "general", label: "General" }
];

const AdminGalleries = () => {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [adminToken] = useState(() => localStorage.getItem("admin_token"));
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "general",
    is_public: true,
    is_featured: false,
    tags: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/galleries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGalleries(response.data);
    } catch (error) {
      toast.error("Failed to fetch galleries");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/galleries`, {
        ...formData,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Gallery created!");
      setCreateDialogOpen(false);
      setFormData({ title: "", description: "", category: "general", is_public: true, is_featured: false, tags: "" });
      
      // Navigate to the new gallery
      navigate(`/admin/galleries/${response.data.gallery_id}`);
    } catch (error) {
      toast.error("Failed to create gallery");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedGallery) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/galleries/${selectedGallery.gallery_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Gallery deleted");
      setDeleteDialogOpen(false);
      setSelectedGallery(null);
      fetchGalleries();
    } catch (error) {
      toast.error("Failed to delete gallery");
    } finally {
      setSaving(false);
    }
  };

  const toggleFeatured = async (gallery) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/galleries/${gallery.gallery_id}`, {
        is_featured: !gallery.is_featured
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchGalleries();
      toast.success(gallery.is_featured ? "Removed from featured" : "Added to featured");
    } catch (error) {
      toast.error("Failed to update gallery");
    }
  };

  const togglePublic = async (gallery) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/galleries/${gallery.gallery_id}`, {
        is_public: !gallery.is_public
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchGalleries();
      toast.success(gallery.is_public ? "Gallery hidden" : "Gallery made public");
    } catch (error) {
      toast.error("Failed to update gallery");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Showcase Galleries</h1>
          <p className="text-muted-foreground">
            Create galleries to showcase platform capabilities
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Gallery
        </Button>
      </div>

      {/* Galleries Grid */}
      {galleries.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <Images className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No galleries yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first showcase gallery to display on the platform
          </p>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Gallery
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleries.map((gallery) => (
            <div 
              key={gallery.gallery_id}
              className="bg-card rounded-lg border border-border overflow-hidden group"
            >
              {/* Cover Image */}
              <div 
                className="aspect-video bg-muted relative cursor-pointer"
                onClick={() => navigate(`/admin/galleries/${gallery.gallery_id}`)}
              >
                {gallery.cover_photo ? (
                  <img 
                    src={`${API}/admin/galleries/${gallery.gallery_id}/media/${gallery.cover_photo}?token=${adminToken}`}
                    alt={gallery.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Images className="w-12 h-12 text-muted-foreground" />
                  </div>
                )}
                
                {/* Badges */}
                <div className="absolute top-2 left-2 flex gap-2">
                  {gallery.is_featured && (
                    <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded font-medium">
                      Featured
                    </span>
                  )}
                  {!gallery.is_public && (
                    <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded">
                      Hidden
                    </span>
                  )}
                </div>
                
                {/* Asset count */}
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {gallery.asset_count || 0} photos
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4">
                <h3 className="font-medium truncate">{gallery.title}</h3>
                <p className="text-sm text-muted-foreground capitalize">
                  {gallery.category || "General"}
                </p>
                
                {/* Actions */}
                <div className="flex items-center gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/admin/galleries/${gallery.gallery_id}`)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePublic(gallery)}
                    title={gallery.is_public ? "Hide gallery" : "Make public"}
                  >
                    {gallery.is_public ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleFeatured(gallery)}
                    title={gallery.is_featured ? "Remove from featured" : "Add to featured"}
                    className={gallery.is_featured ? "text-yellow-500" : ""}
                  >
                    <Star className="w-4 h-4" fill={gallery.is_featured ? "currentColor" : "none"} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive ml-auto"
                    onClick={() => {
                      setSelectedGallery(gallery);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Showcase Gallery</DialogTitle>
            <DialogDescription>
              Create a gallery to showcase platform capabilities
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Summer Wedding Collection"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the gallery..."
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="e.g., outdoor, romantic, sunset"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="is_public">Public Gallery</Label>
              <Switch
                id="is_public"
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="is_featured">Featured on Homepage</Label>
              <Switch
                id="is_featured"
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gallery</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedGallery?.title}"? This will permanently delete all {selectedGallery?.asset_count || 0} photos in this gallery.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGalleries;
